import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SupportWidget from '../components/SupportWidget';
import {
  ArrowLeft,
  Brush,
  CalendarDays,
  Copy,
  Crown,
  Download,
  Gift,
  Heart,
  ImagePlus,
  Link2,
  MapPin,
  MessageCircleHeart,
  QrCode,
  Sparkles,
  Type,
  Trash2,
  Wand2,
  UsersRound
} from 'lucide-react';
import { TEMPLATE_LIBRARY, TEMPLATE_PRESETS, TEMPLATE_RENDERERS } from '../components/dashboard/creative/templates';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

const DEFAULT_COVER_IMAGE =
  'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1400&q=80';

const ART_TYPES = [
  {
    id: 'save_the_date',
    title: 'Save the date',
    description: 'Avise seus convidados com uma arte rápida e elegante.',
    icon: CalendarDays,
    tag: 'Mais usado'
  },
  {
    id: 'digital_invite',
    title: 'Convite digital',
    description: 'Monte um convite com nome, data, local e visual do casamento.',
    icon: Heart,
    tag: 'Premium'
  },
  {
    id: 'gift_list',
    title: 'Lista de presentes',
    description: 'Crie uma peça pronta para compartilhar sua lista com mais estilo.',
    icon: Gift,
    tag: 'Novo'
  },
  {
    id: 'rsvp',
    title: 'Confirmação de presença',
    description: 'Envie uma arte com chamada clara para confirmação de presença.',
    icon: UsersRound,
    tag: 'Essencial'
  }
];

const FORMAT_OPTIONS = [
  { id: 'story', label: 'Story 9:16', width: 1080, height: 1920, previewClass: 'aspect-[9/16]' },
  { id: 'post', label: 'Post 1:1', width: 1080, height: 1080, previewClass: 'aspect-square' },
  { id: 'invite', label: 'Convite 4:5', width: 1200, height: 1500, previewClass: 'aspect-[4/5]' },
  { id: 'whatsapp', label: 'Card WhatsApp', width: 1080, height: 1350, previewClass: 'aspect-[4/5]' }
];

const getTemplateById = (templateId) => TEMPLATE_LIBRARY.find((template) => template.id === templateId);

const getDefaultTemplateForCategory = (category) =>
  TEMPLATE_LIBRARY.find((template) => template.category === category) || TEMPLATE_LIBRARY[0];

const normalizeHexColor = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toUpperCase();
  }
  return null;
};

const isLightColor = (hexColor) => {
  const normalized = normalizeHexColor(hexColor);
  if (!normalized) return false;
  const numeric = Number.parseInt(normalized.slice(1), 16);
  const red = (numeric >> 16) & 255;
  const green = (numeric >> 8) & 255;
  const blue = numeric & 255;
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance > 0.62;
};

const mixHex = (hexA, hexB, ratio = 0.5) => {
  const a = normalizeHexColor(hexA) || '#000000';
  const b = normalizeHexColor(hexB) || '#FFFFFF';
  const ra = Number.parseInt(a.slice(1, 3), 16);
  const ga = Number.parseInt(a.slice(3, 5), 16);
  const ba = Number.parseInt(a.slice(5, 7), 16);
  const rb = Number.parseInt(b.slice(1, 3), 16);
  const gb = Number.parseInt(b.slice(3, 5), 16);
  const bb = Number.parseInt(b.slice(5, 7), 16);
  const blend = (first, second) => Math.round(first + ((second - first) * ratio));
  return `#${[blend(ra, rb), blend(ga, gb), blend(ba, bb)]
    .map((item) => item.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
};

const getTemplatePreset = (template) => {
  const presetId = template?.presetId;
  return (presetId && TEMPLATE_PRESETS[presetId]) || TEMPLATE_PRESETS.default;
};

const getRecommendedTextColor = ({ template, baseColor, accentColor, fallback }) => {
  const preset = getTemplatePreset(template);
  const backgroundMode = template?.backgroundMode || preset.backgroundStyle?.mode || 'photo';
  const mixed = mixHex(baseColor || template?.baseColor, accentColor || template?.accentColor, 0.45);
  const mixedIsLight = isLightColor(mixed);
  const { lightText = '#FFF9F5', darkText = '#231D1A' } = preset.paletteRules || {};
  const baseOpacity = preset.overlayStyle?.baseOpacity ?? 0.46;
  const accentOpacity = preset.overlayStyle?.accentOpacity ?? 0.26;
  const overlayWeight = (baseOpacity + accentOpacity) / 2;
  const shouldUseDark =
    backgroundMode === 'gradient' || backgroundMode === 'solid'
      ? mixedIsLight
      : (mixedIsLight && overlayWeight > 0.46) || (!mixedIsLight && overlayWeight > 0.7);
  if (shouldUseDark) return darkText;
  if (!shouldUseDark) return lightText;
  return fallback || darkText;
};

const TEXT_COLOR_PRESETS = ['#1C1C1C', '#FFFFFF', '#5E4736', '#2E3E58', '#7A3F53'];

const asInputDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().split('T')[0];
};

const formatEventDate = (value) => {
  if (!value) return 'Escolha a data';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const formatEventDateSafe = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const getSiteLink = (site) => {
  if (!site?.slug) return '';
  return `${window.location.origin}/s/${site.slug}`;
};

const randomId = () => `art_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const makeDraft = ({ category, template, site, user, sourceDraft }) => {
  const fallbackCouple = sourceDraft?.coupleNames || site?.couple_names || user?.name || 'Nome do casal';
  const fallbackDate = sourceDraft?.eventDate || asInputDate(site?.wedding_date);
  const fallbackTime = sourceDraft?.eventTime || site?.ceremony_time || '16:00';
  const fallbackLocation = sourceDraft?.location || site?.ceremony_location || 'Local da celebração';
  const fallbackPhrase =
    sourceDraft?.phrase ||
    'Publique seu site, compartilhe o link e acompanhe tudo em um só lugar.';

  return {
    id: randomId(),
    category,
    templateId: template.id,
    format: template.format,
    layout: template.layout,
    title: `${ART_TYPES.find((type) => type.id === category)?.title || 'Arte'} - ${template.name}`,
    coupleNames: fallbackCouple,
    eventDate: fallbackDate,
    eventTime: fallbackTime,
    location: fallbackLocation,
    phrase: fallbackPhrase,
    ctaText: sourceDraft?.ctaText || 'Acesse nosso site',
    showQrCode: sourceDraft?.showQrCode ?? true,
    showSiteLink: sourceDraft?.showSiteLink ?? true,
    siteLink: sourceDraft?.siteLink || getSiteLink(site),
    imageUrl: sourceDraft?.imageUrl || site?.hero_image || template.sampleImage || DEFAULT_COVER_IMAGE,
    baseColor: sourceDraft?.baseColor || template.baseColor,
    accentColor: sourceDraft?.accentColor || template.accentColor,
    textColor:
      sourceDraft?.textColor ||
      template.textColor ||
      getRecommendedTextColor({
        template,
        baseColor: sourceDraft?.baseColor || template.baseColor,
        accentColor: sourceDraft?.accentColor || template.accentColor
      }),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

const hexToRgba = (hex, alpha) => {
  if (!hex || !hex.startsWith('#')) return `rgba(28, 28, 28, ${alpha})`;
  const normalized = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;
  const bigint = Number.parseInt(normalized.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.closePath();
};

const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight, maxLines = 3) => {
  const words = (text || '').split(' ');
  const lines = [];
  let line = '';

  for (let index = 0; index < words.length; index += 1) {
    const testLine = line ? `${line} ${words[index]}` : words[index];
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = words[index];
      if (lines.length >= maxLines - 1) break;
    } else {
      line = testLine;
    }
  }

  if (line && lines.length < maxLines) lines.push(line);
  lines.forEach((currentLine, lineIndex) => ctx.fillText(currentLine, x, y + lineIndex * lineHeight));
  return lines.length * lineHeight;
};

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

const drawFakeQr = (ctx, x, y, size, accentColor) => {
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(x, y, size, size);
  ctx.fillStyle = accentColor || '#C8A97E';
  const module = size / 9;
  for (let row = 0; row < 9; row += 1) {
    for (let col = 0; col < 9; col += 1) {
      const shouldFill = (row * col + row + col) % 2 === 0;
      if (shouldFill) {
        ctx.fillRect(x + col * module, y + row * module, module * 0.9, module * 0.9);
      }
    }
  }
};

export default function CreativeStudioPage() {
  const { user } = useAuth();
  const imageInputRef = useRef(null);
  const editorSectionRef = useRef(null);
  const editorPulseTimeoutRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [editorPulse, setEditorPulse] = useState(false);

  useEffect(() => {
    const loadContext = async () => {
      try {
        const siteRes = await axios.get(`${API}/sites/my`, { withCredentials: true });
        const foundSite = siteRes.data || null;
        setSite(foundSite);

        const seededDrafts = ART_TYPES.map((type) =>
          makeDraft({
            category: type.id,
            template: getDefaultTemplateForCategory(type.id),
            site: foundSite,
            user
          })
        );
        setDrafts(seededDrafts);
        setActiveDraftId(seededDrafts[0]?.id || null);
      } catch (error) {
        const seededDrafts = ART_TYPES.map((type) =>
          makeDraft({
            category: type.id,
            template: getDefaultTemplateForCategory(type.id),
            site: null,
            user
          })
        );
        setDrafts(seededDrafts);
        setActiveDraftId(seededDrafts[0]?.id || null);
      } finally {
        setLoading(false);
      }
    };

    loadContext();
  }, [user]);

  useEffect(() => () => {
    if (editorPulseTimeoutRef.current) {
      window.clearTimeout(editorPulseTimeoutRef.current);
    }
  }, []);

  const activeDraft = useMemo(
    () => drafts.find((draft) => draft.id === activeDraftId) || null,
    [drafts, activeDraftId]
  );

  const activeTemplate = activeDraft ? getTemplateById(activeDraft.templateId) : null;
  const activePreset = getTemplatePreset(activeTemplate);
  const supportedFormatIds = activePreset.supportedFormats || FORMAT_OPTIONS.map((format) => format.id);
  const supportedFormatOptions = FORMAT_OPTIONS.filter((format) => supportedFormatIds.includes(format.id));
  const activeFormat =
    supportedFormatOptions.find((format) => format.id === activeDraft?.format) ||
    FORMAT_OPTIONS.find((format) => format.id === activeDraft?.format) ||
    FORMAT_OPTIONS[0];
  const hasDedicatedRenderer = Boolean(activeTemplate && TEMPLATE_RENDERERS[activeTemplate.id]);
  const availableTemplates = TEMPLATE_LIBRARY.filter(
    (template) => template.category === activeDraft?.category
  );
  const hasEventDate = Boolean(activeDraft?.eventDate);
  const hasEventTime = Boolean(activeDraft?.eventTime?.trim());
  const hasLocation = Boolean(activeDraft?.location?.trim());
  const hasPhrase = Boolean(activeDraft?.phrase?.trim());
  const dateTimeLabel = [hasEventDate ? formatEventDateSafe(activeDraft?.eventDate) : '', hasEventTime ? activeDraft?.eventTime?.trim() : '']
    .filter(Boolean)
    .join(' - ');
  const activeLayoutType = activePreset.layoutType || 'bella_romantic';
  const activeLayoutVariant = activeDraft?.layout || 'center';
  const activeBackgroundMode = activeTemplate?.backgroundMode || activePreset.backgroundStyle?.mode || 'photo';
  const overlayStrengthByLayout = {
    bella_romantic: { from: 0.36, to: 0.2, vignette: 0.08 },
    jardim_natural: { from: 0.75, to: 0.56, vignette: 0.04 },
    lumina_editorial: { from: 0.5, to: 0.18, vignette: 0.28 },
    petala_poetic: { from: 0.3, to: 0.14, vignette: 0.06 }
  };
  const overlayConfig = overlayStrengthByLayout[activeLayoutType] || {};
  const previewOverlayFrom = overlayConfig.from ?? (activePreset.overlayStyle?.baseOpacity ?? 0.48);
  const previewOverlayTo = overlayConfig.to ?? (activePreset.overlayStyle?.accentOpacity ?? 0.28);
  const previewVignette = overlayConfig.vignette ?? (activePreset.overlayStyle?.vignette ?? 0.12);
  const imageTreatment = activePreset.imageTreatment || {};
  const previewImageFilter = `brightness(${imageTreatment.brightness ?? 0.96}) contrast(${imageTreatment.contrast ?? 1.04}) saturate(${imageTreatment.saturate ?? 0.95}) blur(${imageTreatment.blur ?? 0}px)`;
  const previewTextColor =
    normalizeHexColor(activeDraft?.textColor) ||
    getRecommendedTextColor({
      template: activeTemplate,
      baseColor: activeDraft?.baseColor,
      accentColor: activeDraft?.accentColor
    });
  const previewTextShadow = isLightColor(previewTextColor)
    ? '0 2px 14px rgba(0,0,0,0.45)'
    : '0 2px 12px rgba(255,255,255,0.2)';
  const previewGradientBackground = `linear-gradient(${activePreset.backgroundStyle?.gradientAngle ?? 155}deg, ${hexToRgba(activeDraft?.baseColor || '#E8B7B1', 0.95)}, ${hexToRgba(activeDraft?.accentColor || '#C8A97E', 0.82)})`;
  const previewSolidBackground = hexToRgba(activeDraft?.baseColor || '#E8B7B1', 0.95);
  const previewShouldRenderPhoto = activeBackgroundMode === 'photo';
  const isTemplateHeaderDark = isLightColor(previewTextColor);
  const previewTypeBadgeClass = isTemplateHeaderDark
    ? 'bg-white/90 text-[#4F3832]'
    : 'bg-white/20 text-white';
  const previewPremiumBadgeClass = isTemplateHeaderDark
    ? 'bg-[#2E1F25]/85 text-[#FFF3E2]'
    : 'bg-[#FFF4E5] text-[#9C784C]';
  const previewFrameClass =
    activeLayoutType === 'lumina_editorial'
      ? 'rounded-[18px]'
      : activeLayoutType === 'jardim_natural'
        ? 'rounded-[26px]'
        : 'rounded-[30px]';
  const hasCta = Boolean(activeDraft?.ctaText?.trim());
  const hasSiteLink = Boolean(activeDraft?.showSiteLink && activeDraft?.siteLink?.trim());
  const previewMetaItems = [
    dateTimeLabel ? { id: 'date', label: dateTimeLabel, icon: CalendarDays } : null,
    hasLocation ? { id: 'location', label: activeDraft.location.trim(), icon: MapPin } : null
  ].filter(Boolean);

  const renderPreviewOrnaments = () => {
    const ornamentType = activePreset.ornamentStyle?.type;
    if (ornamentType === 'botanical') {
      return (
        <>
          <div className="absolute top-8 left-6 h-20 w-20 rounded-full border border-[#8D6B4F]/30 bg-[#FFF8F1]/45" />
          <div className="absolute top-14 left-14 h-10 w-10 rounded-full border border-[#8D6B4F]/25 bg-white/25" />
          <div className="absolute bottom-10 right-8 h-24 w-24 rounded-full border border-[#8D6B4F]/28 bg-[#FFF4EA]/35" />
          <div className="absolute bottom-16 right-20 h-10 w-10 rounded-full border border-[#8D6B4F]/18 bg-white/25" />
          <div className="absolute top-[18%] right-[14%] h-px w-20 bg-[#8D6B4F]/38 rotate-[-14deg]" />
          <div className="absolute bottom-[22%] left-[14%] h-px w-16 bg-[#8D6B4F]/34 rotate-[18deg]" />
        </>
      );
    }
    if (ornamentType === 'editorial-lines') {
      return (
        <>
          <div className="absolute inset-y-0 left-0 w-[38%] bg-black/24" />
          <div className="absolute inset-y-0 right-0 w-[34%] bg-[#1C151A]/62" />
          <div className="absolute top-0 left-[38%] h-full w-px bg-white/28" />
          <div className="absolute top-0 right-[34%] h-full w-px bg-[#D3AC84]/75" />
          <div className="absolute top-14 right-10 h-24 w-px bg-white/45" />
        </>
      );
    }
    if (ornamentType === 'poetic-blur') {
      return (
        <>
          <div className="absolute -top-10 left-6 h-36 w-36 rounded-full bg-[#F7E8E0]/40 blur-3xl" />
          <div className="absolute top-[22%] -right-12 h-28 w-28 rounded-full bg-[#FFECE2]/35 blur-3xl" />
          <div className="absolute -bottom-12 left-[20%] h-28 w-28 rounded-full bg-[#F3D8CD]/34 blur-3xl" />
          <div className="absolute bottom-[28%] left-[54%] h-20 w-20 rounded-full bg-white/16 blur-2xl" />
        </>
      );
    }
    return (
      <>
        <div className="absolute -top-14 -right-14 h-40 w-40 rounded-full bg-white/22 blur-2xl" />
        <div className="absolute bottom-0 left-0 h-28 w-28 rounded-full bg-[#FDEBE8]/28 blur-2xl" />
      </>
    );
  };

  const renderPreviewComposition = () => {
    const artTypeLabel = ART_TYPES.find((type) => type.id === activeDraft.category)?.title;
    const PreviewTemplateComponent = activeTemplate ? TEMPLATE_RENDERERS[activeTemplate.id] : null;
    const templateData = {
      coupleName: activeDraft.coupleNames || 'Nome do casal',
      eventDate: activeDraft.eventDate || '',
      eventTime: activeDraft.eventTime || '',
      dateLabel: dateTimeLabel,
      location: hasLocation ? activeDraft.location.trim() : '',
      rows: previewMetaItems,
      shortPhrase: hasPhrase ? activeDraft.phrase.trim() : '',
      ctaText: hasCta ? activeDraft.ctaText.trim() : '',
      siteUrl: activeDraft.siteLink || '',
      showQrCode: Boolean(activeDraft.showQrCode),
      showSiteLink: Boolean(hasSiteLink),
      baseColor: activeDraft.baseColor,
      accentColor: activeDraft.accentColor,
      textColor: previewTextColor,
      coverImage: activeDraft.imageUrl || DEFAULT_COVER_IMAGE,
      format: activeDraft.format,
      artTypeLabel,
      isPremium: Boolean(activeTemplate?.premium || activeTemplate?.isPremium),
      titleStyle: {
        color: previewTextColor,
        textShadow: previewTextShadow,
        fontFamily: activePreset.titleStyle?.family || '"Playfair Display", serif',
        letterSpacing: activePreset.titleStyle?.tracking || '0.01em'
      },
      bodyStyle: {
        style: {
          color: previewTextColor,
          textShadow: previewTextShadow,
          fontFamily: activePreset.bodyStyle?.family || 'Inter, sans-serif',
          lineHeight: activePreset.bodyStyle?.lineHeight || '1.55'
        },
        metaFamily: activePreset.metaStyle?.family || 'Inter, sans-serif',
        metaTracking: activePreset.metaStyle?.tracking || '0.08em',
        metaUppercase: activePreset.metaStyle?.uppercase || false
      },
      contrastStrategy: activePreset.contrastStrategy || 'smart-overlay'
    };

    if (PreviewTemplateComponent) {
      return <PreviewTemplateComponent data={templateData} />;
    }

    return (
      <div className="absolute inset-0 p-6 flex items-center justify-center">
        <div className="rounded-2xl border border-white/60 bg-white/84 px-6 py-8 text-center shadow-[0_18px_34px_rgba(36,24,20,0.22)]">
          <p className="text-sm text-[#5E514F]">Template indisponível para visualização.</p>
        </div>
      </div>
    );
  };

  const updateActiveDraft = (partial) => {
    if (!activeDraft) return;
    setDrafts((currentDrafts) =>
      currentDrafts.map((draft) =>
        draft.id === activeDraft.id
          ? { ...draft, ...partial, updatedAt: new Date().toISOString() }
          : draft
      )
    );
  };

  const focusEditor = () => {
    window.requestAnimationFrame(() => {
      if (!editorSectionRef.current) return;
      editorSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setEditorPulse(true);
      if (editorPulseTimeoutRef.current) {
        window.clearTimeout(editorPulseTimeoutRef.current);
      }
      editorPulseTimeoutRef.current = window.setTimeout(() => setEditorPulse(false), 1400);
    });
  };

  const openEditorForCategory = (category) => {
    const existingDraft = drafts.find((draft) => draft.category === category);
    if (existingDraft) {
      setActiveDraftId(existingDraft.id);
      focusEditor();
      return;
    }

    const newDraft = makeDraft({
      category,
      template: getDefaultTemplateForCategory(category),
      site,
      user
    });
    setDrafts((current) => [newDraft, ...current]);
    setActiveDraftId(newDraft.id);
    focusEditor();
  };

  const duplicateDraft = (category, sourceDraft) => {
    const baseDraft = sourceDraft || drafts.find((draft) => draft.category === category);
    const template = getTemplateById(baseDraft?.templateId) || getDefaultTemplateForCategory(category);
    const duplicated = makeDraft({
      category,
      template,
      site,
      user,
      sourceDraft: baseDraft
    });
    setDrafts((current) => [duplicated, ...current]);
    setActiveDraftId(duplicated.id);
    focusEditor();
    toast.success('Rascunho duplicado e aberto no editor.');
  };

  const deleteDraft = (draftId) => {
    const draft = drafts.find((item) => item.id === draftId);
    if (!draft) return;
    const confirmed = window.confirm(`Excluir o rascunho "${draft.title}"?`);
    if (!confirmed) return;

    setDrafts((currentDrafts) => {
      const remainingDrafts = currentDrafts.filter((item) => item.id !== draftId);
      if (remainingDrafts.length === 0) {
        const fallbackCategory = draft.category || ART_TYPES[0].id;
        const fallback = makeDraft({
          category: fallbackCategory,
          template: getDefaultTemplateForCategory(fallbackCategory),
          site,
          user
        });
        setActiveDraftId(fallback.id);
        return [fallback];
      }

      if (activeDraftId === draftId) {
        setActiveDraftId(remainingDrafts[0].id);
      }

      return remainingDrafts;
    });

    toast.success('Rascunho removido.');
  };

  const handleTemplateChange = (templateId) => {
    const template = getTemplateById(templateId);
    if (!template) return;
    const previousTemplateImage = activeTemplate?.sampleImage;
    const currentImage = activeDraft?.imageUrl;
    const shouldSwapImage =
      !currentImage ||
      currentImage === previousTemplateImage ||
      currentImage === DEFAULT_COVER_IMAGE;
    updateActiveDraft({
      templateId: template.id,
      format: template.format,
      layout: template.layout,
      baseColor: template.baseColor,
      accentColor: template.accentColor,
      imageUrl: shouldSwapImage ? (template.sampleImage || currentImage || DEFAULT_COVER_IMAGE) : currentImage,
      textColor: template.textColor || getRecommendedTextColor({
        template,
        baseColor: template.baseColor,
        accentColor: template.accentColor
      }),
      title: `${ART_TYPES.find((type) => type.id === template.category)?.title || 'Arte'} - ${template.name}`
    });
  };

  const applyAutomaticTextColor = () => {
    if (!activeDraft) return;
    updateActiveDraft({
      textColor: getRecommendedTextColor({
        template: activeTemplate,
        baseColor: activeDraft.baseColor,
        accentColor: activeDraft.accentColor,
        fallback: activeDraft.textColor
      })
    });
    toast.success('Cor do texto ajustada para melhor contraste.');
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem válido.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateActiveDraft({ imageUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const saveDraft = () => {
    setSavingDraft(true);
    window.setTimeout(() => {
      setSavingDraft(false);
      toast.success('Rascunho salvo neste navegador.');
    }, 500);
  };

  const exportImage = async () => {
    if (!activeDraft) return;
    setExporting(true);

    try {
      const format = FORMAT_OPTIONS.find((item) => item.id === activeDraft.format) || FORMAT_OPTIONS[0];
      const exportHasEventDate = Boolean(activeDraft.eventDate);
      const exportHasEventTime = Boolean(activeDraft.eventTime?.trim());
      const exportHasLocation = Boolean(activeDraft.location?.trim());
      const exportHasPhrase = Boolean(activeDraft.phrase?.trim());
      const exportHasSiteLink = Boolean(activeDraft.showSiteLink && activeDraft.siteLink?.trim());
      const exportHasCta = Boolean(activeDraft.ctaText?.trim());
      const exportPreset = getTemplatePreset(activeTemplate);
      const exportLayoutType = exportPreset.layoutType || 'bella_romantic';
      const exportLayoutVariant = activeDraft.layout || 'center';
      const exportBackgroundMode = activeTemplate?.backgroundMode || exportPreset.backgroundStyle?.mode || 'photo';
      const exportOverlayFrom = exportPreset.overlayStyle?.baseOpacity ?? 0.48;
      const exportOverlayTo = exportPreset.overlayStyle?.accentOpacity ?? 0.28;
      const exportVignette = exportPreset.overlayStyle?.vignette ?? 0.12;
      const exportTextColor =
        normalizeHexColor(activeDraft.textColor) ||
        getRecommendedTextColor({
          template: activeTemplate,
          baseColor: activeDraft.baseColor,
          accentColor: activeDraft.accentColor,
          fallback: '#231D1A'
        });
      const exportMetaColor = hexToRgba(exportTextColor, 0.92);
      const canvas = document.createElement('canvas');
      canvas.width = format.width;
      canvas.height = format.height;
      const ctx = canvas.getContext('2d');

      if (exportBackgroundMode === 'photo') {
        const image = await loadImage(activeDraft.imageUrl);
        if (image) {
          const ratio = Math.max(canvas.width / image.width, canvas.height / image.height);
          const drawWidth = image.width * ratio;
          const drawHeight = image.height * ratio;
          const dx = (canvas.width - drawWidth) / 2;
          const dy = (canvas.height - drawHeight) / 2;
          ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
        } else {
          const fallbackGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
          fallbackGradient.addColorStop(0, activeDraft.baseColor || '#E8B7B1');
          fallbackGradient.addColorStop(1, activeDraft.accentColor || '#C8A97E');
          ctx.fillStyle = fallbackGradient;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      } else if (exportBackgroundMode === 'gradient') {
        const gradientBackground = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradientBackground.addColorStop(0, hexToRgba(activeDraft.baseColor, 0.95));
        gradientBackground.addColorStop(1, hexToRgba(activeDraft.accentColor, 0.82));
        ctx.fillStyle = gradientBackground;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else {
        ctx.fillStyle = hexToRgba(activeDraft.baseColor, 0.95);
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const overlayGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      overlayGradient.addColorStop(0, hexToRgba(activeDraft.baseColor, exportOverlayFrom));
      overlayGradient.addColorStop(1, hexToRgba(activeDraft.accentColor, exportOverlayTo));
      ctx.fillStyle = overlayGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = `rgba(0,0,0,${exportVignette})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (exportLayoutType === 'jardim_natural') {
        ctx.strokeStyle = 'rgba(154, 122, 89, 0.28)';
        ctx.lineWidth = Math.max(2, Math.round(canvas.width * 0.0022));
        drawRoundedRect(
          ctx,
          Math.round(canvas.width * 0.08),
          Math.round(canvas.height * 0.1),
          Math.round(canvas.width * 0.84),
          Math.round(canvas.height * 0.8),
          Math.round(canvas.width * 0.03)
        );
        ctx.stroke();
      }
      if (exportLayoutType === 'lumina_editorial') {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.24)';
        ctx.fillRect(0, 0, Math.round(canvas.width * 0.38), canvas.height);
        ctx.fillStyle = 'rgba(30, 22, 27, 0.64)';
        ctx.fillRect(Math.round(canvas.width * 0.66), 0, Math.round(canvas.width * 0.34), canvas.height);
        ctx.fillStyle = 'rgba(204, 171, 136, 0.65)';
        ctx.fillRect(Math.round(canvas.width * 0.38), 0, 2, canvas.height);
        ctx.fillRect(Math.round(canvas.width * 0.66), 0, 2, canvas.height);
      }
      if (exportLayoutType === 'petala_poetic') {
        const halo = ctx.createRadialGradient(canvas.width * 0.72, canvas.height * 0.2, 20, canvas.width * 0.72, canvas.height * 0.2, canvas.width * 0.42);
        halo.addColorStop(0, 'rgba(255,255,255,0.35)');
        halo.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = halo;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const padding = Math.round(canvas.width * 0.06);
      const contentWidth = canvas.width - (padding * 2);
      const typeLabel = (ART_TYPES.find((type) => type.id === activeDraft.category)?.title || 'Convite').toUpperCase();
      const exportTitleScale = exportLayoutType === 'lumina_editorial'
        ? 0.066
        : exportLayoutType === 'petala_poetic'
          ? 0.072
          : exportLayoutType === 'jardim_natural'
            ? 0.07
            : 0.074;

      drawRoundedRect(ctx, padding, padding, Math.round(canvas.width * 0.42), Math.round(canvas.width * 0.09), Math.round(canvas.width * 0.045));
      ctx.fillStyle = isLightColor(exportTextColor) ? 'rgba(255, 255, 255, 0.88)' : 'rgba(255, 255, 255, 0.24)';
      ctx.fill();
      ctx.fillStyle = isLightColor(exportTextColor) ? '#4F3832' : '#FFFFFF';
      ctx.font = `700 ${Math.round(canvas.width * 0.024)}px Inter`;
      ctx.fillText(typeLabel, padding + Math.round(canvas.width * 0.035), padding + Math.round(canvas.width * 0.059));

      if (activeTemplate?.premium) {
        const premiumWidth = Math.round(canvas.width * 0.24);
        const premiumHeight = Math.round(canvas.width * 0.09);
        const premiumX = canvas.width - padding - premiumWidth;
        drawRoundedRect(ctx, premiumX, padding, premiumWidth, premiumHeight, Math.round(canvas.width * 0.045));
        ctx.fillStyle = isLightColor(exportTextColor) ? 'rgba(46,31,37,0.9)' : '#FFF4E5';
        ctx.fill();
        ctx.fillStyle = isLightColor(exportTextColor) ? '#FFF3E2' : '#9C784C';
        ctx.font = `700 ${Math.round(canvas.width * 0.022)}px Inter`;
        ctx.fillText('PREMIUM', premiumX + Math.round(canvas.width * 0.05), padding + Math.round(canvas.width * 0.059));
      }

      const drawMeta = (originX, originY, centered = false) => {
        let cursor = originY;
        ctx.fillStyle = exportMetaColor;
        ctx.font = `${Math.round(canvas.width * 0.034)}px Inter`;
        ctx.textAlign = centered ? 'center' : 'left';
        if (exportHasEventDate || exportHasEventTime) {
          const exportDateTime = [exportHasEventDate ? formatEventDateSafe(activeDraft.eventDate) : '', exportHasEventTime ? activeDraft.eventTime.trim() : '']
            .filter(Boolean)
            .join(' - ');
          ctx.fillText(exportDateTime, originX, cursor);
          cursor += Math.round(canvas.width * 0.056);
        }
        if (exportHasLocation) {
          drawWrappedText(
            ctx,
            activeDraft.location.trim(),
            originX,
            cursor,
            Math.round(contentWidth * (centered ? 0.88 : 0.76)),
            Math.round(canvas.width * 0.039),
            2
          );
          cursor += Math.round(canvas.width * 0.074);
        }
        return cursor;
      };

      const drawFooter = ({ leftX, bottomY, maxWidth, darkSurface }) => {
        if (exportHasSiteLink) {
          const linkWidth = Math.round(maxWidth);
          const linkHeight = Math.round(canvas.width * 0.09);
          ctx.fillStyle = darkSurface ? 'rgba(0,0,0,0.58)' : 'rgba(255,255,255,0.9)';
          drawRoundedRect(ctx, leftX, bottomY - linkHeight, linkWidth, linkHeight, Math.round(canvas.width * 0.022));
          ctx.fill();
          ctx.fillStyle = activeDraft.accentColor || '#9A6B65';
          ctx.font = `700 ${Math.round(canvas.width * 0.017)}px Inter`;
          ctx.fillText('LINK DO SITE', leftX + Math.round(canvas.width * 0.022), bottomY - Math.round(canvas.width * 0.058));
          ctx.fillStyle = darkSurface ? '#FFFFFF' : exportTextColor;
          ctx.font = `600 ${Math.round(canvas.width * 0.024)}px Inter`;
          drawWrappedText(
            ctx,
            activeDraft.siteLink || 'vireinoiva.com.br',
            leftX + Math.round(canvas.width * 0.022),
            bottomY - Math.round(canvas.width * 0.028),
            linkWidth - Math.round(canvas.width * 0.04),
            Math.round(canvas.width * 0.028),
            1
          );
        }

        if (activeDraft.showQrCode) {
          const qrSize = Math.round(canvas.width * 0.132);
          const qrX = canvas.width - padding - qrSize;
          const qrY = canvas.height - padding - qrSize;
          drawFakeQr(ctx, qrX, qrY, qrSize, activeDraft.accentColor);
        }
      };

      ctx.fillStyle = exportTextColor;
      ctx.font = `700 ${Math.round(canvas.width * exportTitleScale)}px "Playfair Display", serif`;
      if (exportLayoutType === 'jardim_natural') {
        const panelW = Math.round(canvas.width * 0.84);
        const panelH = Math.round(canvas.height * 0.74);
        const panelX = Math.round((canvas.width - panelW) / 2);
        const panelY = exportLayoutVariant === 'bottom-card'
          ? Math.round(canvas.height - panelH - canvas.height * 0.08)
          : Math.round(canvas.height * 0.14);
        drawRoundedRect(ctx, panelX, panelY, panelW, panelH, Math.round(canvas.width * 0.03));
        ctx.fillStyle = 'rgba(255,249,245,0.9)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(154,122,89,0.28)';
        ctx.lineWidth = Math.max(2, Math.round(canvas.width * 0.002));
        ctx.stroke();

        const titleY = panelY + Math.round(canvas.height * 0.12);
        ctx.textAlign = 'center';
        drawWrappedText(ctx, activeDraft.coupleNames || 'Nome do casal', panelX + Math.round(panelW / 2), titleY, Math.round(panelW * 0.8), Math.round(canvas.width * 0.084), 2);
        let cursor = drawMeta(panelX + Math.round(panelW / 2), panelY + Math.round(canvas.height * 0.28), true);
        if (exportHasPhrase) {
          ctx.fillStyle = exportMetaColor;
          ctx.font = `${Math.round(canvas.width * 0.033)}px Inter`;
          drawWrappedText(ctx, activeDraft.phrase.trim(), panelX + Math.round(panelW / 2), cursor, Math.round(panelW * 0.76), Math.round(canvas.width * 0.04), 3);
        }
        if (exportHasCta) {
          ctx.font = `700 ${Math.round(canvas.width * 0.018)}px Inter`;
          ctx.fillStyle = exportTextColor;
          ctx.fillText(activeDraft.ctaText.trim().toUpperCase(), panelX + Math.round(panelW / 2), panelY + panelH - Math.round(canvas.height * 0.14));
        }
        ctx.fillStyle = 'rgba(154,122,89,0.35)';
        ctx.fillRect(panelX + Math.round(panelW * 0.42), panelY + panelH - Math.round(canvas.height * 0.2), Math.round(panelW * 0.16), 1);
        drawFooter({ leftX: panelX + Math.round(canvas.width * 0.02), bottomY: panelY + panelH - Math.round(canvas.width * 0.02), maxWidth: canvas.width * 0.5, darkSurface: false });
      } else if (exportLayoutType === 'lumina_editorial') {
        const sideSplitX = Math.round(canvas.width * 0.42);
        ctx.fillStyle = 'rgba(19,13,17,0.74)';
        ctx.fillRect(sideSplitX, 0, canvas.width - sideSplitX, canvas.height);
        ctx.fillStyle = 'rgba(255,255,255,0.22)';
        ctx.fillRect(sideSplitX, 0, 1, canvas.height);
        ctx.fillStyle = 'rgba(210,170,124,0.58)';
        ctx.fillRect(sideSplitX + Math.round(canvas.width * 0.01), 0, 1, canvas.height);

        const panelW = exportLayoutVariant === 'split'
          ? Math.round(canvas.width * 0.56)
          : Math.round(canvas.width * 0.82);
        const panelH = exportLayoutVariant === 'split'
          ? Math.round(canvas.height * 0.9)
          : Math.round(canvas.height * 0.78);
        const panelX = exportLayoutVariant === 'split'
          ? Math.round(canvas.width - panelW - canvas.width * 0.035)
          : Math.round((canvas.width - panelW) / 2);
        const panelY = exportLayoutVariant === 'split'
          ? Math.round(canvas.height * 0.05)
          : Math.round(canvas.height * 0.12);
        drawRoundedRect(ctx, panelX, panelY, panelW, panelH, Math.round(canvas.width * 0.024));
        ctx.fillStyle = 'rgba(0,0,0,0.28)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.18)';
        ctx.lineWidth = Math.max(2, Math.round(canvas.width * 0.0018));
        ctx.stroke();

        const titleY = panelY + Math.round(panelH * 0.52);
        ctx.textAlign = 'left';
        drawWrappedText(ctx, activeDraft.coupleNames || 'Nome do casal', panelX + Math.round(canvas.width * 0.035), titleY, Math.round(panelW * 0.86), Math.round(canvas.width * 0.088), 2);
        let cursor = drawMeta(panelX + Math.round(canvas.width * 0.035), titleY + Math.round(canvas.width * 0.07), false);
        if (exportHasPhrase) {
          ctx.fillStyle = exportMetaColor;
          ctx.font = `${Math.round(canvas.width * 0.031)}px Inter`;
          drawWrappedText(ctx, activeDraft.phrase.trim(), panelX + Math.round(canvas.width * 0.035), cursor, Math.round(panelW * 0.84), Math.round(canvas.width * 0.038), 4);
        }
        if (exportHasCta) {
          ctx.fillStyle = exportTextColor;
          ctx.font = `700 ${Math.round(canvas.width * 0.017)}px Inter`;
          const ctaY = panelY + panelH - Math.round(canvas.height * 0.16);
          ctx.fillText(activeDraft.ctaText.trim().toUpperCase(), panelX + Math.round(canvas.width * 0.035), ctaY);
          ctx.strokeStyle = 'rgba(255,255,255,0.45)';
          ctx.lineWidth = Math.max(1, Math.round(canvas.width * 0.0016));
          ctx.beginPath();
          ctx.moveTo(panelX + Math.round(canvas.width * 0.035), ctaY + Math.round(canvas.width * 0.008));
          ctx.lineTo(panelX + Math.round(canvas.width * 0.25), ctaY + Math.round(canvas.width * 0.008));
          ctx.stroke();
        }
        drawFooter({ leftX: panelX + Math.round(canvas.width * 0.035), bottomY: panelY + panelH - Math.round(canvas.width * 0.015), maxWidth: canvas.width * 0.32, darkSurface: true });
      } else if (exportLayoutType === 'petala_poetic') {
        const panelW = exportLayoutVariant === 'frame'
          ? Math.round(canvas.width * 0.86)
          : Math.round(canvas.width * 0.8);
        const panelH = exportLayoutVariant === 'frame'
          ? Math.round(canvas.height * 0.48)
          : Math.round(canvas.height * 0.44);
        const panelX = Math.round((canvas.width - panelW) / 2);
        const panelY = exportLayoutVariant === 'frame'
          ? Math.round(canvas.height * 0.39)
          : Math.round(canvas.height * 0.42);
        drawRoundedRect(ctx, panelX, panelY, panelW, panelH, Math.round(canvas.width * 0.042));
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.58)';
        ctx.lineWidth = Math.max(2, Math.round(canvas.width * 0.002));
        ctx.stroke();

        const titleY = panelY + Math.round(canvas.height * 0.12);
        ctx.textAlign = 'center';
        drawWrappedText(ctx, activeDraft.coupleNames || 'Nome do casal', panelX + Math.round(panelW / 2), titleY, Math.round(panelW * 0.8), Math.round(canvas.width * 0.084), 2);
        let cursor = drawMeta(panelX + Math.round(panelW / 2), panelY + Math.round(canvas.height * 0.24), true);
        if (exportHasPhrase) {
          ctx.fillStyle = exportMetaColor;
          ctx.font = `${Math.round(canvas.width * 0.031)}px Inter`;
          drawWrappedText(ctx, activeDraft.phrase.trim(), panelX + Math.round(panelW / 2), cursor, Math.round(panelW * 0.76), Math.round(canvas.width * 0.038), 3);
        }
        if (exportHasCta) {
          ctx.fillStyle = exportTextColor;
          ctx.font = `700 ${Math.round(canvas.width * 0.017)}px Inter`;
          ctx.fillText(activeDraft.ctaText.trim().toUpperCase(), panelX + Math.round(panelW / 2), panelY + panelH - Math.round(canvas.height * 0.14));
        }
        drawFooter({ leftX: panelX + Math.round(canvas.width * 0.03), bottomY: panelY + panelH - Math.round(canvas.width * 0.015), maxWidth: canvas.width * 0.52, darkSurface: false });
      } else {
        const panelW = exportLayoutVariant === 'split'
          ? Math.round(canvas.width * 0.78)
          : Math.round(canvas.width * 0.82);
        const panelH = exportLayoutVariant === 'bottom-card'
          ? Math.round(canvas.height * 0.5)
          : Math.round(canvas.height * 0.54);
        const panelX = exportLayoutVariant === 'split'
          ? Math.round(canvas.width - panelW - canvas.width * 0.08)
          : Math.round(canvas.width * 0.08);
        const panelY = exportLayoutVariant === 'bottom-card'
          ? Math.round(canvas.height * 0.35)
          : Math.round(canvas.height * 0.23);
        drawRoundedRect(ctx, panelX, panelY, panelW, panelH, Math.round(canvas.width * 0.035));
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.62)';
        ctx.lineWidth = Math.max(2, Math.round(canvas.width * 0.002));
        ctx.stroke();

        ctx.fillStyle = 'rgba(209,174,136,0.48)';
        ctx.fillRect(panelX + Math.round(canvas.width * 0.018), panelY + Math.round(canvas.height * 0.03), 1, panelH - Math.round(canvas.height * 0.06));
        ctx.fillRect(panelX + Math.round(canvas.width * 0.018), panelY + Math.round(canvas.height * 0.03), Math.round(canvas.width * 0.06), 1);

        const titleY = panelY + Math.round(canvas.height * 0.14);
        ctx.textAlign = 'left';
        drawWrappedText(ctx, activeDraft.coupleNames || 'Nome do casal', panelX + Math.round(canvas.width * 0.065), titleY, Math.round(panelW * 0.83), Math.round(canvas.width * 0.086), 2);
        let cursor = drawMeta(panelX + Math.round(canvas.width * 0.065), panelY + Math.round(canvas.height * 0.26), false);
        if (exportHasPhrase) {
          ctx.fillStyle = exportMetaColor;
          ctx.font = `${Math.round(canvas.width * 0.033)}px Inter`;
          drawWrappedText(ctx, activeDraft.phrase.trim(), panelX + Math.round(canvas.width * 0.065), cursor, Math.round(panelW * 0.76), Math.round(canvas.width * 0.04), 3);
        }
        if (exportHasCta) {
          ctx.fillStyle = exportTextColor;
          ctx.font = `700 ${Math.round(canvas.width * 0.018)}px Inter`;
          ctx.fillText(activeDraft.ctaText.trim().toUpperCase(), panelX + Math.round(canvas.width * 0.065), panelY + panelH - Math.round(canvas.height * 0.13));
        }
        drawFooter({ leftX: panelX + Math.round(canvas.width * 0.04), bottomY: panelY + panelH - Math.round(canvas.width * 0.015), maxWidth: canvas.width * 0.5, darkSurface: false });
      }

      ctx.textAlign = 'left';

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${(activeDraft.title || 'convite')
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')}.png`;
      link.click();
      toast.success('Imagem exportada em PNG.');
    } catch (error) {
      toast.error('Não foi possível exportar agora. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F1EF] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#E8B7B1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#333333]">Carregando Convites & Artes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1EF]">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#EADCD8]">
        <div className="container-wedding h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-[#333333] hover:text-[#1C1C1C]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-xl md:text-2xl font-bold">Convites & Artes</h1>
            <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-[#FDEBE8] px-3 py-1 text-xs font-semibold text-[#9A6B65]">
              <Crown className="w-3 h-3" />
              Experiência Premium
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => duplicateDraft(activeDraft?.category || ART_TYPES[0].id, activeDraft)}>
              <Copy className="w-4 h-4 mr-2" />
              Duplicar
            </Button>
            <Button className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white" onClick={exportImage} disabled={!activeDraft || exporting}>
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exportando...' : 'Exportar imagem'}
            </Button>
          </div>
        </div>
      </header>

      <main className="container-wedding py-8 space-y-8">
        <section className="card-dashboard bg-gradient-to-br from-white to-[#FDF8F6]">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold mb-2">Convites & Artes</h2>
              <p className="text-[#333333] max-w-3xl">
                Personalize save the date, convite digital e artes de RSVP com uma experiência premium pronta para exportar.
              </p>
            </div>
            <Button className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white w-full lg:w-auto" onClick={() => openEditorForCategory(ART_TYPES[0].id)}>
              <Sparkles className="w-4 h-4 mr-2" />
              Criar nova arte
            </Button>
          </div>
        </section>

        <section className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {ART_TYPES.map((type) => {
            const typeTemplate = getDefaultTemplateForCategory(type.id);
            const Icon = type.icon;
            return (
              <article key={type.id} className="card-dashboard flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-2xl bg-[#FDEBE8] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#9A6B65]" />
                  </div>
                  <span className="rounded-full bg-[#FFF7EC] text-[#9C784C] text-xs font-semibold px-3 py-1">{type.tag}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{type.title}</h3>
                  <p className="text-sm text-[#333333] mt-1">{type.description}</p>
                </div>
                <div className="rounded-xl border border-[#EADCD8] bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-[#9A6B65] font-semibold">Template sugerido</p>
                  <p className="font-semibold mt-1">{typeTemplate.name}</p>
                  <p className="text-xs text-[#333333]">{typeTemplate.subtitle}</p>
                </div>
                <div className="mt-auto flex items-center gap-2">
                  <Button className="flex-1 bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white" onClick={() => openEditorForCategory(type.id)}>
                    Editar
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => duplicateDraft(type.id)}>
                    Duplicar
                  </Button>
                </div>
              </article>
            );
          })}
        </section>

        <section className="card-dashboard bg-gradient-to-r from-[#FDEBE8] via-[#F9F3F1] to-[#FFFDFB]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center border border-[#EADCD8]">
              <Wand2 className="w-6 h-6 text-[#C8A97E]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">Materiais prontos para compartilhar</h3>
              <p className="text-[#333333] mt-1">
                Crie peças elegantes para WhatsApp, Instagram e convite digital com linguagem de papelaria premium.
              </p>
            </div>
          </div>
        </section>

        {activeDraft && (
          <section
            ref={editorSectionRef}
            className={`space-y-4 rounded-2xl transition-all duration-500 ${
              editorPulse ? 'ring-2 ring-[#E8B7B1] ring-offset-2 ring-offset-[#F6F1EF]' : ''
            }`}
          >
            <div className="card-dashboard border-dashed bg-[#FFF9F7]">
              <p className="text-xs uppercase tracking-[0.14em] text-[#9A6B65] font-semibold">Editor ativo</p>
              <h3 className="text-xl font-semibold mt-1">
                Agora você está editando: {ART_TYPES.find((type) => type.id === activeDraft.category)?.title}
              </h3>
              <p className="text-sm text-[#333333] mt-1">
                Altere textos, cores e foto no painel da direita e exporte a arte quando finalizar.
              </p>
            </div>

            <div className="grid gap-6 xl:grid-cols-[280px_minmax(0,1fr)_340px]">
            <aside className="card-dashboard h-fit">
              <h3 className="text-lg font-semibold mb-3">Templates</h3>
              <p className="text-sm text-[#333333] mb-4">Escolha uma direção visual de papelaria digital premium para seu casamento.</p>
              <div className="space-y-2">
                {availableTemplates.map((template) => {
                  const templatePreset = getTemplatePreset(template);
                  const templateLayoutType = templatePreset.layoutType || 'bella_romantic';
                  const miniPreviewBackground = template.backgroundMode === 'photo'
                    ? `linear-gradient(145deg, ${hexToRgba(template.baseColor, 0.86)}, ${hexToRgba(template.accentColor, 0.58)})`
                    : template.backgroundMode === 'gradient'
                      ? `linear-gradient(165deg, ${hexToRgba(template.baseColor, 0.96)}, ${hexToRgba(template.accentColor, 0.72)})`
                      : hexToRgba(template.baseColor, 0.95);
                  const miniPreviewOverlay = templateLayoutType === 'lumina_editorial'
                    ? 'bg-gradient-to-r from-black/18 via-black/34 to-black/64'
                    : templateLayoutType === 'jardim_natural'
                      ? 'bg-gradient-to-b from-white/15 via-white/35 to-white/65'
                      : templateLayoutType === 'petala_poetic'
                        ? 'bg-gradient-to-b from-white/8 via-white/24 to-white/34'
                        : 'bg-gradient-to-b from-white/8 via-white/18 to-white/26';

                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleTemplateChange(template.id)}
                      className={`w-full rounded-xl border p-3 text-left transition-all ${
                        activeDraft.templateId === template.id
                          ? 'border-[#E8B7B1] bg-[#FDEBE8]'
                          : 'border-[#EADCD8] bg-white hover:border-[#E8B7B1]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-[#1C1C1C]">{template.name}</p>
                        {template.premium && (
                          <span className="text-[10px] uppercase tracking-[0.12em] text-[#9C784C] bg-[#FFF4E5] px-2 py-1 rounded-full">
                            Premium
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#333333]">{template.description || template.subtitle}</p>

                      <div
                        className="relative mt-2 h-12 overflow-hidden rounded-lg border border-white/60 shadow-inner"
                        style={{ background: miniPreviewBackground }}
                      >
                        <div className={`absolute inset-0 ${miniPreviewOverlay}`} />
                        {templateLayoutType === 'lumina_editorial' && (
                          <>
                            <div className="absolute inset-y-0 right-0 w-[54%] bg-black/38" />
                            <div className="absolute inset-y-0 right-[54%] w-px bg-white/45" />
                          </>
                        )}
                        {templateLayoutType === 'jardim_natural' && (
                          <>
                            <div className="absolute inset-y-1 left-[14%] right-[14%] rounded-md border border-[#A8845E]/42 bg-white/35" />
                            <div className="absolute top-2 left-3 h-2 w-2 rounded-full border border-[#8D6B4F]/45 bg-[#FFF8F2]/70" />
                            <div className="absolute bottom-2 right-3 h-2 w-2 rounded-full border border-[#8D6B4F]/45 bg-[#FFF2EA]/65" />
                          </>
                        )}
                        {templateLayoutType === 'petala_poetic' && (
                          <>
                            <div className="absolute left-[12%] right-[12%] bottom-2 h-5 rounded-full border border-white/52 bg-white/26 backdrop-blur-sm" />
                            <div className="absolute -top-2 right-2 h-5 w-5 rounded-full bg-white/24 blur-sm" />
                          </>
                        )}
                        {templateLayoutType === 'bella_romantic' && (
                          <>
                            <div className="absolute left-2 top-2 bottom-2 w-px bg-[#D1AE88]/48" />
                            <div className="absolute left-2 top-2 h-px w-4 bg-[#D1AE88]/48" />
                            <div className="absolute right-2 top-2 h-6 w-[55%] rounded-md bg-white/52" />
                          </>
                        )}
                      </div>

                      <p className="text-[11px] uppercase tracking-[0.12em] text-[#9A6B65] mt-1">{template.styleLabel || 'Premium'}</p>
                      <p className="text-[11px] text-[#7A6A68] mt-1">{template.note || templatePreset.catalogNote}</p>
                      <p className="text-[11px] text-[#8A7774] mt-1">Ideal para: {template.recommendedFor || 'Compartilhar com convidados'}</p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 rounded-xl bg-[#F9F5F4] p-3 border border-[#EADCD8] text-sm text-[#333333]">
                <p className="font-semibold text-[#1C1C1C] mb-1">Dica de conversão</p>
                Use frase curta + CTA objetivo para aumentar cliques no link enviado aos convidados.
              </div>
            </aside>

            <div className="card-dashboard">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[#9A6B65] font-semibold">
                    {ART_TYPES.find((type) => type.id === activeDraft.category)?.title}
                  </p>
                  <h3 className="text-2xl font-bold">{activeDraft.title}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={saveDraft} disabled={savingDraft}>
                    {savingDraft ? 'Salvando...' : 'Salvar rascunho'}
                  </Button>
                  <Button className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white" onClick={exportImage} disabled={exporting}>
                    <Download className="w-4 h-4 mr-2" />
                    {exporting ? 'Exportando...' : 'Exportar'}
                  </Button>
                </div>
              </div>

              <div className="rounded-3xl border border-[#EADCD8] bg-[#F8F4F2] p-4 md:p-6 flex items-center justify-center">
                <div className={`relative w-full max-w-[470px] ${activeFormat.previewClass} overflow-hidden border border-white/60 shadow-xl ${previewFrameClass}`}>
                  {previewShouldRenderPhoto ? (
                    <img
                      src={activeDraft.imageUrl || DEFAULT_COVER_IMAGE}
                      alt="Preview da arte"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ filter: previewImageFilter }}
                    />
                  ) : (
                    <div
                      className="absolute inset-0"
                      style={{
                        background: activeBackgroundMode === 'gradient' ? previewGradientBackground : previewSolidBackground
                      }}
                    />
                  )}
                  <div
                    className="absolute inset-0"
                    style={{ background: `linear-gradient(145deg, ${hexToRgba(activeDraft.baseColor, previewOverlayFrom)}, ${hexToRgba(activeDraft.accentColor, previewOverlayTo)})` }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{ boxShadow: `inset 0 0 0 9999px rgba(0,0,0,${previewVignette})` }}
                  />
                  {!hasDedicatedRenderer && renderPreviewOrnaments()}
                  {renderPreviewComposition()}
                </div>
              </div>
            </div>

            <aside className="card-dashboard h-fit space-y-4">
              <h3 className="text-lg font-semibold">Personalização</h3>

              <div className="space-y-3">
                <div>
                  <Label>Nome do casal</Label>
                  <Input value={activeDraft.coupleNames} onChange={(event) => updateActiveDraft({ coupleNames: event.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Data</Label>
                    <Input type="date" value={activeDraft.eventDate} onChange={(event) => updateActiveDraft({ eventDate: event.target.value })} />
                  </div>
                  <div>
                    <Label>Horário</Label>
                    <Input type="time" value={activeDraft.eventTime} onChange={(event) => updateActiveDraft({ eventTime: event.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Local</Label>
                  <Input value={activeDraft.location} onChange={(event) => updateActiveDraft({ location: event.target.value })} />
                </div>
                <div>
                  <Label>Frase curta</Label>
                  <Textarea value={activeDraft.phrase} onChange={(event) => updateActiveDraft({ phrase: event.target.value })} rows={3} />
                </div>
                <div>
                  <Label>CTA opcional</Label>
                  <Input value={activeDraft.ctaText} onChange={(event) => updateActiveDraft({ ctaText: event.target.value })} />
                </div>
              </div>

              <div className="space-y-3 border-t border-[#EADCD8] pt-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="flex items-center gap-1"><Brush className="w-3.5 h-3.5" />Cor base</Label>
                    <Input type="color" value={activeDraft.baseColor} onChange={(event) => updateActiveDraft({ baseColor: event.target.value })} className="h-10 px-2" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" />Cor destaque</Label>
                    <Input type="color" value={activeDraft.accentColor} onChange={(event) => updateActiveDraft({ accentColor: event.target.value })} className="h-10 px-2" />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-1"><Type className="w-3.5 h-3.5" />Cor do texto</Label>
                    <button
                      type="button"
                      className="text-xs font-semibold text-[#9A6B65] hover:text-[#7F5954]"
                      onClick={applyAutomaticTextColor}
                    >
                      Auto contraste
                    </button>
                  </div>
                  <Input
                    type="color"
                    value={normalizeHexColor(activeDraft.textColor) || previewTextColor}
                    onChange={(event) => updateActiveDraft({ textColor: event.target.value })}
                    className="h-10 px-2"
                  />
                  <div className="flex flex-wrap gap-2">
                    {TEXT_COLOR_PRESETS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => updateActiveDraft({ textColor: color })}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] ${
                          (normalizeHexColor(activeDraft.textColor) || previewTextColor) === color
                            ? 'border-[#E8B7B1] bg-[#FDEBE8] text-[#7F5954]'
                            : 'border-[#EADCD8] bg-white text-[#5F514F] hover:border-[#E8B7B1]'
                        }`}
                      >
                        <span className="h-3 w-3 rounded-full border border-black/10" style={{ backgroundColor: color }} />
                        {color}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#7A6A68]">
                    Se o texto ficar fraco, use o botão Auto contraste.
                  </p>
                </div>

                <div>
                  <Label>Formato da arte</Label>
                  <Select value={activeDraft.format} onValueChange={(value) => updateActiveDraft({ format: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {supportedFormatOptions.map((format) => (
                        <SelectItem key={format.id} value={format.id}>
                          {format.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Foto principal</Label>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" className="w-full" onClick={() => imageInputRef.current?.click()}>
                      <ImagePlus className="w-4 h-4 mr-2" />
                      Trocar foto
                    </Button>
                    <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-1"><Link2 className="w-3.5 h-3.5" />Link do site</Label>
                  <Input value={activeDraft.siteLink} onChange={(event) => updateActiveDraft({ siteLink: event.target.value })} placeholder="https://vireinoiva.com.br/s/seu-site" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 border-t border-[#EADCD8] pt-4">
                <Button
                  type="button"
                  variant={activeDraft.showQrCode ? 'default' : 'outline'}
                  className={activeDraft.showQrCode ? 'bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]' : ''}
                  onClick={() => updateActiveDraft({ showQrCode: !activeDraft.showQrCode })}
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  QR Code
                </Button>
                <Button
                  type="button"
                  variant={activeDraft.showSiteLink ? 'default' : 'outline'}
                  className={activeDraft.showSiteLink ? 'bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]' : ''}
                  onClick={() => updateActiveDraft({ showSiteLink: !activeDraft.showSiteLink })}
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Link
                </Button>
              </div>
            </aside>
            </div>
          </section>
        )}

        <section className="card-dashboard">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">Rascunhos recentes</h3>
            <span className="text-sm text-[#333333]">{drafts.length} criações</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {drafts.slice(0, 6).map((draft) => (
              <article
                key={draft.id}
                className={`rounded-xl border p-4 text-left transition-all ${
                  activeDraftId === draft.id
                    ? 'border-[#E8B7B1] bg-[#FDEBE8]'
                    : 'border-[#EADCD8] bg-white hover:border-[#E8B7B1]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] font-semibold text-[#9A6B65]">
                      {ART_TYPES.find((type) => type.id === draft.category)?.title}
                    </p>
                    <p className="font-semibold mt-1">{draft.title}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-[#9A6B65] hover:text-[#B94747] hover:bg-[#FDEBE8]"
                    onClick={() => deleteDraft(draft.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <button
                  type="button"
                  className="w-full text-left mt-2"
                  onClick={() => {
                    setActiveDraftId(draft.id);
                    focusEditor();
                  }}
                >
                  <p className="text-xs text-[#333333]">
                    Atualizado em {new Date(draft.updatedAt).toLocaleDateString('pt-BR')}
                  </p>
                  <p className="text-xs text-[#9A6B65] mt-2 font-semibold uppercase tracking-[0.12em]">Abrir no editor</p>
                </button>
              </article>
            ))}
          </div>
        </section>

        <section className="card-dashboard bg-white">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-[#EADCD8] p-4 bg-[#FFF9F7]">
              <p className="font-semibold flex items-center gap-2"><MessageCircleHeart className="w-4 h-4 text-[#9A6B65]" />WhatsApp pronto</p>
              <p className="text-sm text-[#333333] mt-1">Crie e exporte em segundos para enviar aos convidados.</p>
            </div>
            <div className="rounded-xl border border-[#EADCD8] p-4 bg-[#FFFCF6]">
              <p className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#9C784C]" />Visual consistente</p>
              <p className="text-sm text-[#333333] mt-1">Use as mesmas cores e linguagem do seu site em todas as peças.</p>
            </div>
            <div className="rounded-xl border border-[#EADCD8] p-4 bg-[#F8F9FF]">
              <p className="font-semibold flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#7A86B6]" />Fluxo guiado</p>
              <p className="text-sm text-[#333333] mt-1">Editor leve para casamento: rápido, claro e sem complexidade desnecessária.</p>
            </div>
          </div>
        </section>
      </main>
      <SupportWidget />
    </div>
  );
}

